import React, { useEffect, useState, ReactNode } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import ChangeNetworkDrawer from "../../Modules/ChangeNetworkDrawer";
import NetworkUnsupportedModal from "../../Modules/NetworkUnsupportedModal";
import PreflightModalTransfer from "../../Modules/PreflightModalTransfer";
import AddressInput from "../Custom/AddressInput";
import TransferActiveModal from "../../Modules/TransferActiveModal";
import { useAccount, useChainId, useSwitchChain } from "wagmi";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import {
  ChainbridgeProvider,
  useChainbridge,
} from "../../Contexts/ChainbridgeContext";
import TokenSelectInput from "../Custom/TokenSelectInput";
import TokenInput from "../Custom/TokenInput";
import { useTokenBalances } from "../../hooks/useTokenBalances";

import { ethers, utils } from "ethers";
import { chainbridgeConfig, isPalmNetwork } from "../../chainbridgeConfig";
import Fees from "../Custom/Fees";
import PageHeading from "../UI/PageHeading";
import {
  Heading,
  Text,
  Field,
  Box,
  Stack,
  VStack,
  Flex,
  Button,
  Image,
  Alert,
  Separator,
  Link,
} from "@chakra-ui/react";
import { toaster } from "../../config/toaster";
import { ExternalLink, Clock } from "lucide-react";
import { toastSuccess, toastError } from "../../config/toast";

import PATCH from "../../media/logos/patch.png";
import CHAINSAFE from "../../media/logos/chainsafe.png";
import CONSENSYS from "../../media/logos/consensys.svg";
import CurrentGasFees from "../Custom/CurrentGasFees";

import { getFeatureFlagStatus } from "../../featureFlags";

const shadowForkMessagesEnabled = getFeatureFlagStatus(
  "shadowForkMessagesEnabled"
);

type IntermediateFormValues = {
  tokenAmount: string;
  token: string;
  receiver: string;
};

type PreflightDetails = {
  tokenAmount: number;
  token: string;
  tokenSymbol: string;
  receiver: string;
};

const ethNativeTokenSymbol = "ETH";

const TransferPage = () => {
  const { address, isConnected: isReady } = useAccount();
  const chainId = useChainId();
  const { switchChainAsync } = useSwitchChain();
  const { openConnectModal } = useConnectModal();
  const {
    homeChain,
    destinationChains,
    destinationChain,
    deposit,
    setDestinationChain,
    transactionStatus,
    resetDeposit,
    bridgeFee,
    homeChainIsPalm,
    palmRelayFee,
    palmGreenFee,
    tollTokenSymbol,
    tollTokenDecimals,
    tollTokenAddress,
  } = useChainbridge();

  const tokensMap = useTokenBalances(homeChain?.tokens ?? []);

  const [switchingNetworks, setSwitchingNetworks] = useState<boolean>(false);
  const [walletConnecting, setWalletConnecting] = useState(false);
  const [preflightModalOpen, setPreflightModalOpen] = useState<boolean>(false);
  const [ethereumSettedAsDefault, setEthereumSettedAsDefault] =
    useState<boolean>(false);

  const [intermediateFormValues, setIntermediateFormValues] =
    useState<IntermediateFormValues>({
      receiver: "",
      token: "",
      tokenAmount: "",
    });

  const [preflightDetails, setPreflightDetails] = useState<PreflightDetails>({
    receiver: "",
    token: "",
    tokenAmount: 0,
    tokenSymbol: "",
  });

  useEffect(() => {
    let tokenAmountAsNumber = parseFloat(intermediateFormValues.tokenAmount);
    setPreflightDetails({
      ...intermediateFormValues,
      tokenAmount: isNaN(tokenAmountAsNumber) ? 0 : tokenAmountAsNumber,
      tokenSymbol: tokensMap[intermediateFormValues.token]?.symbol || "",
    });
  }, [intermediateFormValues]);

  useEffect(() => {
    if (!isReady) return;
    const supportedNetworks = chainbridgeConfig.chains.map(
      (bc) => bc.networkId
    );
    const isSupported = supportedNetworks.includes(chainId);
    const isInPalm = isPalmNetwork(chainId);

    if (!isSupported || (isInPalm && !ethereumSettedAsDefault)) {
      const ethChain = chainbridgeConfig.chains.find(
        (chain) => chain.nativeTokenSymbol === ethNativeTokenSymbol
      );
      if (ethChain) {
        handleSwitchNetwork(ethChain.name, ethChain.networkId);
        setEthereumSettedAsDefault(true);
      }
    }
  }, [isReady, chainId]);

  const handleConnect = () => {
    openConnectModal?.();
  };

  const handleSwitchNetwork = async (
    chainName?: string,
    networkId?: number
  ) => {
    const id = networkId || destinationChain?.networkId;
    const name = (networkId && chainName) || destinationChain?.name;

    const showToast = (success: boolean) => {
      const title = success
        ? `Chain changed to ${name}`
        : `Sorry, there was an error changing chains`;
      const conf = success ? toastSuccess : toastError;
      return toaster.create({
        title,
        type: conf.status,
        duration: conf.duration,
      });
    };

    if (id) {
      setSwitchingNetworks(true);
      switchChainAsync({ chainId: id })
        .then(() => showToast(true))
        .catch(() => showToast(false))
        .finally(() => setSwitchingNetworks(false));
    }
  };

  const DECIMALS =
    preflightDetails && tokensMap[preflightDetails.token]
      ? tokensMap[preflightDetails.token]!.decimals
      : 18;

  const REGEX =
    DECIMALS > 0
      ? new RegExp(`^[0-9]{1,18}(\\.[0-9]{1,${DECIMALS}})?$`)
      : new RegExp(`^[0-9]{1,18}$`);

  const totalFees = () => {
    const relayFee = homeChainIsPalm
      ? parseInt(ethers.utils.formatUnits(palmRelayFee, tollTokenDecimals))
      : bridgeFee;
    const greenFee = homeChainIsPalm
      ? parseInt(ethers.utils.formatUnits(palmGreenFee, tollTokenDecimals))
      : 0;
    return relayFee ? relayFee + greenFee : greenFee;
  };

  const transferSchema = z.object({
    tokenAmount: z
      .string()
      .min(1, "Please set a value")
      .refine(
        (value) =>
          !!value &&
          preflightDetails &&
          tokensMap[preflightDetails.token] &&
          tokensMap[preflightDetails.token]!.balance !== undefined,
        "Please select a token"
      )
      .refine((value) => {
        try {
          return REGEX.test(`${value}`);
        } catch {
          return false;
        }
      }, "Input invalid")
      .refine(
        (value) =>
          !!(
            value &&
            preflightDetails &&
            tokensMap[preflightDetails.token] &&
            parseFloat(value) <= tokensMap[preflightDetails.token]!.balance
          ),
        "Insufficient funds"
      )
      .refine((value) => !!value && parseFloat(value) > 0, "Less than minimum")
      .refine((value) => {
        const bridgeFees = totalFees();
        const transferTokenMatchesTollToken =
          tollTokenAddress &&
          preflightDetails &&
          preflightDetails.token === tollTokenAddress;
        const transferTokens =
          value && transferTokenMatchesTollToken ? parseFloat(value) : 0;
        const feeToken = tollTokenAddress && tokensMap[tollTokenAddress];
        if (bridgeFees == 0 || !homeChainIsPalm) return true;
        if (feeToken && feeToken.balance) {
          return bridgeFees + transferTokens <= feeToken.balance;
        }
        return false;
      }, "Insufficient funds to pay bridge fees"),
    token: z.string().min(1, "Please select a token"),
    receiver: z
      .string()
      .min(1, "Please add a receiving address")
      .refine((value) => utils.isAddress(value), "Please add a valid address"),
  });

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
    reset,
  } = useForm<IntermediateFormValues>({
    mode: "onBlur",
    resolver: zodResolver(transferSchema),
  });

  const resetForm = async () => {
    setIntermediateFormValues({
      receiver: "",
      token: "",
      tokenAmount: "",
    });
    setPreflightDetails({
      receiver: "",
      token: "",
      tokenAmount: 0,
      tokenSymbol: "",
    });
    reset({ token: "" });
  };

  return (
    <>
      <PageHeading title="Token Bridge" />
      {shadowForkMessagesEnabled && (
        <Box mb="16px" pl="8px" color="palmNetwork.100" bg="palmNetwork.800">
          <Box pb="32px" pt="48px" maxW="728px" pl="16px">
            <Heading fontSize="24px" mb="16px">
              Shadow Fork Testnet testing
            </Heading>
            <Text mb="16px">
              This application has been updated so that the Palm-Goerli testnet
              bridge now interacts with the temporary &apos;shadow fork&apos;
              Palm Testnet. Bridging between Goerli and the official Palm
              Testnet is currently suspended whilst this testing is carried out.
            </Text>
            <Text mb="16px">
              To use this bridge when sending tokens from the shadow fork Palm
              Testnet to Goerli, you will need to update your wallet to use the
              shadow fork Palm Testnet RPC - this can be added from the
              Configure page.
            </Text>
            <Text mb="16px">
              Please refer to the Palm network documentation for details at{" "}
              <Link>https://docs.palm.network/network-update</Link>
            </Text>
          </Box>
        </Box>
      )}

      <Box mt="8" mb="12" maxW="730px" ml="18px">
        <Heading fontSize="24px" mr="2">
          Transfer DAI, WETH and other tokens to and from Palm
        </Heading>
        <Text mt="8" mb="8">
          The Palm bridge can be used to transfer DAI and other ERC-20 tokens
          between the Palm network and Ethereum. When assets are transferred
          from Ethereum to Palm, the bridge will top up the destination address
          with a small amount of PALM.
        </Text>
        <Text>
          A fee is required to move assets across the bridge from Palm to
          Ethereum, charged in DAI. This fee covers the cost of gas on Ethereum,
          and also goes towards carbon offset projects to account for the
          emissions associated with the part of this transaction that takes
          place on the Ethereum network.
        </Text>
      </Box>
      <Box
        width="540px"
        bg="white"
        border="1px solid"
        borderColor="brand.300"
        p="32px"
        ml="18px"
      >
        {!isReady ? (
          <Button onClick={handleConnect}>Connect Wallet</Button>
        ) : walletConnecting ? (
          <p>
            This app requires access to your wallet, <br />
            please login and authorize access to continue.
          </p>
        ) : (
          <>
            <NetworkDisplay location="Origin Network" name={homeChain?.name} />
            <Button
              width="0px"
              mt="8px"
              mb="10px"
              onClick={() => handleSwitchNetwork()}
              disabled={switchingNetworks}
            >
              ↑↓
            </Button>
            <NetworkDisplay
              location="Destination network"
              name={destinationChain?.name}
            />
          </>
        )}
        <form
          onSubmit={handleSubmit(async () => {
            setPreflightModalOpen(true);
          })}
        >
          <Stack gap="50px" direction="column" mt="34px">
            <Stack gap="24px" direction="row">
              <Field.Root invalid={!!errors?.token?.message} required>
                <Field.Label>Token</Field.Label>
                <TokenSelectInput
                  tokensMap={tokensMap}
                  name="token"
                  isDisabled={!destinationChain}
                  label={`Balance: `}
                  value={getValues("token")}
                  sync={(tokenAddress) => {
                    setIntermediateFormValues({
                      ...intermediateFormValues,
                      token: tokenAddress,
                    });
                  }}
                  register={register}
                  setValue={setValue}
                  options={
                    Object.keys(tokensMap).map((t) => ({
                      value: t,
                      label: (
                        <div>
                          <span>{tokensMap[t]?.symbol || t}</span>
                        </div>
                      ),
                    })) || []
                  }
                />
                <Field.ErrorText>{errors?.token?.message}</Field.ErrorText>
              </Field.Root>
              <Field.Root invalid={!!errors?.tokenAmount?.message} required>
                <Field.Label>Transfer amount</Field.Label>
                <TokenInput
                  name="tokenAmount"
                  tokenSelectorKey="token"
                  tokensMap={tokensMap}
                  isDisabled={
                    !destinationChain ||
                    !preflightDetails.token ||
                    preflightDetails.token === ""
                  }
                  placeholder={"0"}
                  value={intermediateFormValues.tokenAmount}
                  onChange={(value: string) => {
                    setIntermediateFormValues({
                      ...intermediateFormValues,
                      tokenAmount: value,
                    });
                  }}
                  register={register}
                />
                <Field.ErrorText>
                  {errors?.tokenAmount?.message}
                </Field.ErrorText>
              </Field.Root>
            </Stack>

            <Field.Root invalid={!!errors?.receiver?.message} required>
              <Field.Label>Destination address</Field.Label>
              <AddressInput
                name="receiver"
                isDisabled={!destinationChain}
                placeholder="Please enter the receiving address"
                senderAddress={`${address}`}
                value={intermediateFormValues.receiver}
                onChange={(value: string) => {
                  setIntermediateFormValues({
                    ...intermediateFormValues,
                    receiver: value,
                  });
                }}
                register={register}
                setValue={setValue}
              />
              <Field.ErrorText>{errors?.receiver?.message}</Field.ErrorText>
            </Field.Root>
          </Stack>
          <Box>
            <CurrentGasFees />
          </Box>
          {homeChainIsPalm ? (
            <Alert.Root status="info" mt={4} mb={2}>
              <Clock size={16} style={{ marginRight: "8px" }} />
              <Alert.Content>
                <Alert.Description>
                  Minimum transfer time: 1 hour (256 blocks)
                </Alert.Description>
              </Alert.Content>
            </Alert.Root>
          ) : null}
          <Fees
            relayFee={
              homeChainIsPalm
                ? parseInt(
                    ethers.utils.formatUnits(palmRelayFee, tollTokenDecimals)
                  )
                : bridgeFee
            }
            relayFeeSymbol={
              homeChainIsPalm ? tollTokenSymbol : homeChain?.nativeTokenSymbol
            }
            showGreenFee={homeChainIsPalm}
            greenFee={
              homeChainIsPalm
                ? parseInt(
                    ethers.utils.formatUnits(palmGreenFee, tollTokenDecimals)
                  )
                : undefined
            }
            greenFeeSymbol={homeChainIsPalm ? tollTokenSymbol : undefined}
            transferAmount={
              preflightDetails ? preflightDetails.tokenAmount : undefined
            }
            transferAmountSymbol={
              preflightDetails && tokensMap[preflightDetails.token]
                ? tokensMap[preflightDetails.token]!.symbol
                : undefined
            }
          />
          <Flex justifyContent="center">
            <Button
              type="submit"
              disabled={
                !!errors.receiver || !!errors.token || !!errors.tokenAmount
              }
            >
              Start transfer
            </Button>
          </Flex>
        </form>
        <NetworkUnsupportedModal
          open={!homeChain && isReady}
          network={chainId}
          supportedNetworks={chainbridgeConfig.chains.map((bc) => bc.networkId)}
        />
        <PreflightModalTransfer
          open={preflightModalOpen}
          close={() => setPreflightModalOpen(false)}
          receiver={preflightDetails?.receiver || ""}
          sender={address || ""}
          start={() => {
            setPreflightModalOpen(false);
            preflightDetails &&
              deposit({
                amount: preflightDetails.tokenAmount,
                recipient: preflightDetails.receiver,
                tokenAddress: preflightDetails.token,
              });
          }}
          sourceNetwork={homeChain?.name || ""}
          targetNetwork={destinationChain?.name || ""}
          tokenSymbol={preflightDetails?.tokenSymbol || ""}
          value={preflightDetails?.tokenAmount || 0}
        />
        <TransferActiveModal
          open={!!transactionStatus}
          close={() => {
            resetDeposit();
            resetForm();
          }}
        />
        <Separator borderColor="palmNetwork.100" mt="24px" />
        <Stack
          gap="22px"
          direction="column"
          align="left"
          width="540px"
          mt="36px"
        >
          <Stack gap="8px" direction="row" align="center">
            <Text fontSize="xs">Bridge operator: </Text>
            <Image src={CONSENSYS} height="32px" alt="Consensys" />
            <Text fontSize="xs">Carbon offsets: </Text>
            <Image src={PATCH} width="64px" alt="Patch" />
          </Stack>
          <VStack gap="1px" align="left">
            <Text fontSize="xs">
              <Link
                fontSize="xs"
                href={"https://consensys.net/privacy-policy/"}
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy &nbsp;
                <ExternalLink size={14} />
              </Link>{" "}
              |{" "}
              <Link
                fontSize="xs"
                href={"https://consensys.net/terms-of-use/"}
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms of Use &nbsp;
                <ExternalLink size={14} />
              </Link>
            </Text>
          </VStack>
        </Stack>
      </Box>
    </>
  );
};
export default TransferPage;

interface INetworkDisplay {
  location: string;
  name: any;
}

const NetworkDisplay: React.FC<INetworkDisplay> = ({
  location,
  name,
}: INetworkDisplay) => {
  return (
    <>
      <Text fontSize="12px" mb="4px">
        {location}
      </Text>
      <Heading fontSize="20px">{name}</Heading>
    </>
  );
};
